import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { FALLBACK_CATEGORIES, FALLBACK_CITIES } from "../constants/mcevents";
import { Plus, X, Loader2, Save, Youtube, Instagram, Facebook, Globe, Phone, Eye, Lock, CalendarDays } from "lucide-react";
import { WhatsAppIcon, TikTokIcon } from "../components/SocialIcons";
import { toast } from "sonner";
import ImageUpload from "../components/ImageUpload";
import { Calendar } from "../components/ui/calendar";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const EMPTY = {
  stage_name: "",
  category: "chanteur",
  city: "Conakry",
  country: "Guinée",
  bio: "",
  base_price_gnf: 500000,
  price_max_gnf: 0,
  profile_photo: "",
  cover_image: "",
  photos: [],
  music_links: [],
  videos: [],
  languages: ["Français"],
  years_experience: 1,
  phone_public: "",
  whatsapp: "",
  youtube: "",
  tiktok: "",
  instagram: "",
  facebook: "",
  website: "",
  services: [],
  travel_zone: "",
  availability_note: "",
  unavailable_dates: [],
  booking_conditions: "",
};

const SERVICE_SUGGESTIONS = ["Mariage", "Concert", "Soirée privée", "Événement d'entreprise", "Anniversaire", "Festival", "Baptême", "Animation TV/Radio"];

const label = "text-[11px] font-mono uppercase tracking-wider text-muted-foreground";
const inputCls = "mt-1 w-full input-mc";

function TagList({ items, onRemove, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((l, i) => (
        <span key={i} className="inline-flex items-center gap-1.5 bg-muted px-2.5 py-1 text-xs font-semibold rounded-full">
          {l}
          <button type="button" onClick={() => onRemove(i)} data-testid={`${testidPrefix}-remove-${i}`}><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
  );
}

export default function ArtistOnboarding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [newPhoto, setNewPhoto] = useState("");
  const [newLink, setNewLink] = useState("");
  const [newVideo, setNewVideo] = useState("");
  const [newLang, setNewLang] = useState("");
  const [newService, setNewService] = useState("");
  const [mediaReviews, setMediaReviews] = useState([]);

  useEffect(() => {
    api.get("/artists/me/profile").then((r) => {
      if (r.data.profile) {
        setForm((f) => ({ ...EMPTY, ...r.data.profile }));
        setProfileId(r.data.profile.id);
      }
      setMediaReviews(r.data.media_reviews || []);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const mediaStatus = (type, value) => mediaReviews.find((m) => m.media_type === type && m.value === value)?.status;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addItem = (key, value, setter) => {
    if (!value.trim()) return;
    if ((form[key] || []).includes(value.trim())) { setter(""); return; }
    set(key, [...(form[key] || []), value.trim()]);
    setter("");
  };

  const removeItem = (key, idx) => set(key, form[key].filter((_, i) => i !== idx));

  const unavailableDates = (form.unavailable_dates || []).map((d) => new Date(`${d}T00:00:00`));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/artists/profile", {
        ...form,
        base_price_gnf: Number(form.base_price_gnf) || 0,
        price_max_gnf: Number(form.price_max_gnf) || 0,
        years_experience: Number(form.years_experience) || 0,
      });
      setProfileId(data.profile.id);
      setMediaReviews(data.media_reviews || []);
      toast.success("Profil professionnel enregistré !");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="container-mc py-20 text-center font-mono text-sm uppercase tracking-wider text-muted-foreground">Chargement…</div>;

  return (
    <div className="container-mc py-12" data-testid="artist-onboarding-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline mb-2">Espace professionnel</p>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-2">Gérez votre profil d'artiste</h1>
          <p className="text-muted-foreground max-w-2xl">Un profil complet et professionnel augmente vos chances d'être sollicité. Chaque section est visible sur votre page publique.</p>
        </div>
        {profileId && (
          <Link to={`/artists/${profileId}`} className="btn-outline text-sm" data-testid="ao-view-public">
            <Eye className="w-4 h-4 mr-2" /> Voir mon profil public
          </Link>
        )}
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          {/* 01 IDENTITÉ */}
          <section className="card-mc p-6">
            <p className="overline mb-4">01 · Identité publique</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:row-span-2">
                <ImageUpload
                  value={form.profile_photo}
                  onChange={(v) => set("profile_photo", v)}
                  label="Photo de profil"
                  testid="ao-avatar-upload"
                  aspect="aspect-square"
                />
              </div>
              <div className="md:col-span-2">
                <label className={label}>Nom de scène</label>
                <input data-testid="ao-stage-name" required value={form.stage_name} onChange={(e) => set("stage_name", e.target.value)} className={inputCls} placeholder="Ex: King Alasko" />
              </div>
              <div>
                <label className={label}>Catégorie artistique</label>
                <select data-testid="ao-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                  {FALLBACK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Années d'expérience</label>
                <input data-testid="ao-exp" type="number" min="0" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} className={`${inputCls} font-mono`} />
              </div>
              <div>
                <label className={label}>Ville</label>
                <select data-testid="ao-city" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls}>
                  {FALLBACK_CITIES.map((c) => <option key={c}>{c}</option>)}
                  {!FALLBACK_CITIES.includes(form.city) && <option key={form.city}>{form.city}</option>}
                </select>
              </div>
              <div>
                <label className={label}>Pays</label>
                <input data-testid="ao-country" value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} placeholder="Guinée" />
              </div>
            </div>
          </section>

          {/* 02 BIO */}
          <section className="card-mc p-6">
            <p className="overline mb-4">02 · Biographie</p>
            <textarea data-testid="ao-bio" required rows={6} value={form.bio} onChange={(e) => set("bio", e.target.value)} className="w-full input-mc" placeholder="Présentez votre parcours, votre style, vos plus grandes scènes..." />
          </section>

          {/* 03 CONTACT & RÉSEAUX */}
          <section className="card-mc p-6">
            <p className="overline mb-1">03 · Contact & réseaux sociaux</p>
            <p className="text-xs text-muted-foreground mb-4 flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 text-[hsl(var(--secondary))]" />
              Téléphone et WhatsApp ne sont visibles que par les clients ayant payé la mise en relation.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`${label} flex items-center gap-1.5`}><Phone className="w-3.5 h-3.5" /> Téléphone</label>
                <input data-testid="ao-phone" value={form.phone_public} onChange={(e) => set("phone_public", e.target.value)} className={`${inputCls} font-mono`} placeholder="+224 622 00 00 00" />
              </div>
              <div>
                <label className={`${label} flex items-center gap-1.5`}><WhatsAppIcon className="w-3.5 h-3.5" /> WhatsApp</label>
                <input data-testid="ao-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={`${inputCls} font-mono`} placeholder="+224 622 00 00 00" />
              </div>
              <div>
                <label className={`${label} flex items-center gap-1.5`}><Youtube className="w-3.5 h-3.5" /> YouTube</label>
                <input data-testid="ao-youtube" value={form.youtube} onChange={(e) => set("youtube", e.target.value)} className={inputCls} placeholder="https://youtube.com/@votre-chaine" />
              </div>
              <div>
                <label className={`${label} flex items-center gap-1.5`}><TikTokIcon className="w-3.5 h-3.5" /> TikTok</label>
                <input data-testid="ao-tiktok" value={form.tiktok} onChange={(e) => set("tiktok", e.target.value)} className={inputCls} placeholder="https://tiktok.com/@votre-compte" />
              </div>
              <div>
                <label className={`${label} flex items-center gap-1.5`}><Instagram className="w-3.5 h-3.5" /> Instagram</label>
                <input data-testid="ao-instagram" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} className={inputCls} placeholder="https://instagram.com/votre-compte" />
              </div>
              <div>
                <label className={`${label} flex items-center gap-1.5`}><Facebook className="w-3.5 h-3.5" /> Facebook</label>
                <input data-testid="ao-facebook" value={form.facebook} onChange={(e) => set("facebook", e.target.value)} className={inputCls} placeholder="https://facebook.com/votre-page" />
              </div>
              <div className="md:col-span-2">
                <label className={`${label} flex items-center gap-1.5`}><Globe className="w-3.5 h-3.5" /> Site internet</label>
                <input data-testid="ao-website" value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} placeholder="https://votre-site.com" />
              </div>
            </div>
          </section>

          {/* 04 PRESTATIONS & TARIFS */}
          <section className="card-mc p-6">
            <p className="overline mb-4">04 · Prestations & tarifs</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div>
                <label className={label}>Tarif minimum (GNF)</label>
                <input data-testid="ao-price" required type="number" min="0" value={form.base_price_gnf} onChange={(e) => set("base_price_gnf", e.target.value)} className={`${inputCls} font-mono`} />
              </div>
              <div>
                <label className={label}>Tarif maximum (GNF · optionnel)</label>
                <input data-testid="ao-price-max" type="number" min="0" value={form.price_max_gnf} onChange={(e) => set("price_max_gnf", e.target.value)} className={`${inputCls} font-mono`} placeholder="0 = non précisé" />
              </div>
            </div>
            <div className="mb-5">
              <label className={label}>Types de prestations</label>
              <div className="flex gap-2 mt-1 mb-2">
                <input data-testid="ao-service-input" value={newService} onChange={(e) => setNewService(e.target.value)} placeholder="Ex: Mariage" className="flex-1 input-mc" list="service-suggestions" />
                <datalist id="service-suggestions">
                  {SERVICE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                </datalist>
                <button type="button" data-testid="ao-service-add" onClick={() => addItem("services", newService, setNewService)} className="btn-outline px-4"><Plus className="w-4 h-4" /></button>
              </div>
              <TagList items={form.services || []} onRemove={(i) => removeItem("services", i)} testidPrefix="ao-service" />
            </div>
            <div className="mb-5">
              <label className={label}>Zone géographique de déplacement</label>
              <input data-testid="ao-travel-zone" value={form.travel_zone} onChange={(e) => set("travel_zone", e.target.value)} className={inputCls} placeholder="Ex: Conakry et environs · Toute la Guinée · International" />
            </div>
            <div>
              <label className={label}>Conditions de réservation</label>
              <textarea data-testid="ao-conditions" rows={4} value={form.booking_conditions} onChange={(e) => set("booking_conditions", e.target.value)} className="mt-1 w-full input-mc" placeholder="Ex: Acompte de 50% à la réservation · Réservation au moins 2 semaines à l'avance · Transport et hébergement à la charge de l'organisateur..." />
            </div>
          </section>

          {/* 05 DISPONIBILITÉS */}
          <section className="card-mc p-6">
            <p className="overline mb-1">05 · Disponibilités & calendrier</p>
            <p className="text-xs text-muted-foreground mb-4">Cliquez sur les dates où vous n'êtes <strong>PAS disponible</strong>. Elles apparaîtront barrées sur votre profil public.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <div className="card-mc inline-block" data-testid="ao-calendar">
                <Calendar
                  mode="multiple"
                  locale={fr}
                  selected={unavailableDates}
                  onSelect={(dates) => set("unavailable_dates", (dates || []).map((d) => format(d, "yyyy-MM-dd")))}
                  disabled={{ before: new Date() }}
                />
              </div>
              <div>
                <label className={label}>Note de disponibilité</label>
                <textarea data-testid="ao-availability-note" rows={3} value={form.availability_note} onChange={(e) => set("availability_note", e.target.value)} className="mt-1 w-full input-mc" placeholder="Ex: Disponible les week-ends · Indisponible pendant le Ramadan..." />
                {(form.unavailable_dates || []).length > 0 && (
                  <div className="mt-4">
                    <p className={`${label} mb-2 flex items-center gap-1.5`}><CalendarDays className="w-3.5 h-3.5" /> {form.unavailable_dates.length} date(s) indisponible(s)</p>
                    <TagList
                      items={[...form.unavailable_dates].sort().map((d) => format(new Date(`${d}T00:00:00`), "d MMM yyyy", { locale: fr }))}
                      onRemove={(i) => {
                        const sorted = [...form.unavailable_dates].sort();
                        set("unavailable_dates", form.unavailable_dates.filter((d) => d !== sorted[i]));
                      }}
                      testidPrefix="ao-unavail"
                    />
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* 06 EXTRAITS MUSICAUX */}
          <section className="card-mc p-6">
            <p className="overline mb-4">06 · Extraits musicaux</p>
            <p className="text-xs text-muted-foreground mb-3">Collez des liens YouTube, SoundCloud ou Spotify. Vos extraits seront jouables directement sur votre profil.</p>
            <div className="flex gap-2 mb-3">
              <input data-testid="ao-music-input" value={newLink} onChange={(e) => setNewLink(e.target.value)} placeholder="https://youtube.com/watch?v=..." className="flex-1 input-mc" />
              <button type="button" data-testid="ao-music-add" onClick={() => addItem("music_links", newLink, setNewLink)} className="btn-outline px-4"><Plus className="w-4 h-4" /></button>
            </div>
            <ul className="space-y-2">
              {form.music_links.map((u, i) => (
                <li key={i} className="flex items-center justify-between text-sm bg-muted px-3 py-2 rounded-lg">
                  <span className="truncate flex-1 font-mono text-xs">{u}</span>
                  <button type="button" onClick={() => removeItem("music_links", i)} data-testid={`ao-music-remove-${i}`}><X className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          </section>

          {/* 07 VIDÉOS */}
          <section className="card-mc p-6">
            <p className="overline mb-4">07 · Vidéos</p>
            <p className="text-xs text-muted-foreground mb-3">Liens de vos clips ou prestations filmées (YouTube). Elles seront lisibles sur votre profil.</p>
            <p className="text-xs bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))] font-semibold px-3 py-2 rounded-sm mb-3" data-testid="ao-video-moderation-note">
              ⏳ Chaque nouvelle vidéo est soumise à validation par l'équipe avant d'être visible publiquement.
            </p>
            <div className="flex gap-2 mb-3">
              <input data-testid="ao-video-input" value={newVideo} onChange={(e) => setNewVideo(e.target.value)} placeholder="https://youtube.com/watch?v=..." className="flex-1 input-mc" />
              <button type="button" data-testid="ao-video-add" onClick={() => addItem("videos", newVideo, setNewVideo)} className="btn-outline px-4"><Plus className="w-4 h-4" /></button>
            </div>
            <ul className="space-y-2">
              {(form.videos || []).map((u, i) => {
                const st = mediaStatus("video", u);
                return (
                <li key={i} className="flex items-center justify-between gap-2 text-sm bg-muted px-3 py-2 rounded-lg">
                  <span className="truncate flex-1 font-mono text-xs">{u}</span>
                  {st === "pending" && <span className="text-[10px] font-mono uppercase bg-[hsl(var(--secondary))] text-white px-1.5 py-0.5 rounded-full shrink-0" data-testid={`ao-video-status-${i}`}>En attente</span>}
                  {st === "rejected" && <span className="text-[10px] font-mono uppercase bg-destructive text-white px-1.5 py-0.5 rounded-full shrink-0" data-testid={`ao-video-status-${i}`}>Rejetée</span>}
                  {st === "approved" && <span className="text-[10px] font-mono uppercase bg-[hsl(var(--accent))] text-white px-1.5 py-0.5 rounded-full shrink-0" data-testid={`ao-video-status-${i}`}>Publiée</span>}
                  <button type="button" onClick={() => removeItem("videos", i)} data-testid={`ao-video-remove-${i}`}><X className="w-4 h-4" /></button>
                </li>
              );})}
            </ul>
          </section>

          {/* 08 GALERIE */}
          <section className="card-mc p-6">
            <p className="overline mb-4">08 · Galerie photos</p>
            <p className="text-xs text-muted-foreground mb-3">Ajoutez jusqu'à 8 photos. JPG/PNG/WEBP, 8 Mo max chacune.</p>
            <p className="text-xs bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))] font-semibold px-3 py-2 rounded-sm mb-3" data-testid="ao-photo-moderation-note">
              ⏳ Chaque nouvelle photo est soumise à validation par l'équipe avant d'être visible publiquement.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {form.photos.map((p, i) => {
                const st = mediaStatus("photo", p);
                return (
                <div key={i} className="relative">
                  <ImageUpload
                    value={p}
                    onChange={(v) => {
                      const next = [...form.photos];
                      if (v) next[i] = v; else next.splice(i, 1);
                      set("photos", next);
                    }}
                    label={`Photo ${i + 1}`}
                    testid={`ao-photo-${i}`}
                    aspect="aspect-square"
                  />
                  {st === "pending" && <span className="absolute top-1.5 left-1.5 z-10 text-[9px] font-mono uppercase bg-[hsl(var(--secondary))] text-white px-1.5 py-0.5 rounded-full" data-testid={`ao-photo-status-${i}`}>En attente</span>}
                  {st === "rejected" && <span className="absolute top-1.5 left-1.5 z-10 text-[9px] font-mono uppercase bg-destructive text-white px-1.5 py-0.5 rounded-full" data-testid={`ao-photo-status-${i}`}>Rejetée</span>}
                </div>
              );})}
              {form.photos.length < 8 && (
                <ImageUpload
                  value=""
                  onChange={(v) => v && set("photos", [...form.photos, v])}
                  label="Ajouter"
                  testid={`ao-photo-new`}
                  aspect="aspect-square"
                />
              )}
            </div>
          </section>
        </div>

        {/* SIDEBAR */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-6">
            <section className="card-mc p-6">
              <p className="overline mb-3">Photo de couverture</p>
              <ImageUpload
                value={form.cover_image}
                onChange={(v) => set("cover_image", v)}
                label="Photo principale"
                testid="ao-cover-upload"
                aspect="aspect-[4/3]"
              />
            </section>

            <section className="card-mc p-6">
              <p className="overline mb-3">Langues</p>
              <div className="flex gap-2 mb-3">
                <input data-testid="ao-lang-input" value={newLang} onChange={(e) => setNewLang(e.target.value)} placeholder="Ex: Soussou" className="flex-1 input-mc" />
                <button type="button" data-testid="ao-lang-add" onClick={() => addItem("languages", newLang, setNewLang)} className="btn-outline px-4"><Plus className="w-4 h-4" /></button>
              </div>
              <TagList items={form.languages} onRemove={(i) => removeItem("languages", i)} testidPrefix="ao-lang" />
            </section>

            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="ao-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer mon profil
            </button>
            <p className="text-[11px] text-muted-foreground text-center">Vos modifications sont visibles immédiatement sur votre profil public.</p>
          </div>
        </aside>
      </form>
    </div>
  );
}
